import { FC, useCallback, useEffect, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import config from 'lib/config'
import {
  confirmShopifyPlan,
  fetchBillingStatus,
  openShopifySelectPlan,
  openShopifyViewBilling,
  startStripeCheckout,
  startStripePortal,
  BillingStatusResponse,
} from 'utils/billingApi'
import { canUsePaywall } from 'utils/paywallAccess'

const ALLOW_PREFIXES = ['/billing', '/settings/general', '/support']

type Props = {
  userId: string | number | undefined | null
  children?: React.ReactNode
}

/**
 * When paywall gate is on and status.required, redirect away from client app routes to /billing.
 */
const PaywallGate: FC<Props> = ({ userId, children }) => {
  const history = useHistory()
  const location = useLocation()
  const enabled = canUsePaywall(userId) && !!config.billingApiBase
  const [checked, setChecked] = useState(!enabled)

  useEffect(() => {
    if (!enabled) {
      setChecked(true)
      return
    }
    let cancelled = false
    fetchBillingStatus()
      .then((body) => {
        if (cancelled) return
        setChecked(true)
        if (!body || !body.ok || !body.required) return
        const path = location.pathname || ''
        const allowed = ALLOW_PREFIXES.some((p) => path === p || path.startsWith(p + '/'))
        if (!allowed) {
          history.replace('/billing')
        }
      })
      .catch(() => {
        if (!cancelled) setChecked(true)
      })
    return () => {
      cancelled = true
    }
  }, [enabled, history, location.pathname])

  if (!checked) return null
  return <>{children}</>
}

export default PaywallGate

/**
 * @param userId clientPid for REACT_APP_PAYWALL_RELEASED / REACT_APP_PAYWALL_TESTERS gate
 */
export function useBillingPageActions(userId?: string | number | null) {
  const location = useLocation()
  const history = useHistory()
  const paywallEnabled = canUsePaywall(userId) && !!config.billingApiBase

  const [status, setStatus] = useState<BillingStatusResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const requestGen = useRef(0)

  const applyStatus = useCallback((body: BillingStatusResponse | null, gen: number) => {
    if (gen !== requestGen.current) return
    setStatus(body)
    if (body && !body.ok) setError(body.error || 'Unable to load billing status')
    else setError(null)
  }, [])

  const reload = useCallback(() => {
    const gen = ++requestGen.current
    return fetchBillingStatus()
      .then((body) => {
        applyStatus(body, gen)
      })
      .catch((e) => {
        if (gen !== requestGen.current) return
        setError(e instanceof Error ? e.message : 'Unable to load billing status')
      })
  }, [applyStatus])

  // Initial status load + optional plan_handle confirm, serialized so stale loads cannot win.
  useEffect(() => {
    let cancelled = false
    const gen = ++requestGen.current

    const run = async () => {
      let body: BillingStatusResponse | null = null
      try {
        body = await fetchBillingStatus()
      } catch (e) {
        if (cancelled || gen !== requestGen.current) return
        setError(e instanceof Error ? e.message : 'Unable to load billing status')
        return
      }
      if (cancelled || gen !== requestGen.current) return
      applyStatus(body, gen)

      const params = new URLSearchParams(location.search || '')
      const planHandle = (params.get('plan_handle') || '').trim()
      if (!planHandle) return

      // Gate confirm with REACT_APP_PAYWALL_RELEASED / REACT_APP_PAYWALL_TESTERS + clientPid.
      const clientPid = body && body.ok ? body.clientPid ?? userId : userId
      if (!config.billingApiBase || !canUsePaywall(clientPid) || !paywallEnabled) {
        return
      }

      setBusy(true)
      setError(null)
      const shop = (params.get('shop') || '').trim()
      try {
        const confirmed = await confirmShopifyPlan({
          planHandle,
          shop: shop || undefined,
        })
        if (cancelled || gen !== requestGen.current) return
        if (confirmed && confirmed.ok) {
          applyStatus(confirmed, gen)
        } else {
          setError(confirmed?.error || 'Could not confirm Shopify plan')
        }
      } catch (e) {
        if (cancelled || gen !== requestGen.current) return
        setError(e instanceof Error ? e.message : 'Could not confirm Shopify plan')
      } finally {
        // noUnsafeFinally: no bare return; only finalize when this request is still current.
        if (!cancelled && gen === requestGen.current) {
          setBusy(false)
          history.replace('/billing')
        }
      }
    }

    run()
    return () => {
      cancelled = true
      if (requestGen.current === gen) {
        requestGen.current += 1
      }
    }
  }, [location.search, history, applyStatus, userId, paywallEnabled])

  // After returning from Shopify Admin tab, refresh if webhook / other tab updated DB.
  // Skip while plan_handle is present so focus reload cannot bump requestGen mid-confirm.
  useEffect(() => {
    if (!paywallEnabled) return
    const planHandle = new URLSearchParams(location.search || '').get('plan_handle')
    if (planHandle && String(planHandle).trim() !== '') return

    const onFocus = () => {
      reload()
    }
    const onVis = () => {
      if (document.visibilityState === 'visible') reload()
    }
    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onVis)
    return () => {
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onVis)
    }
  }, [reload, paywallEnabled, location.search])

  const selectPlan = useCallback(async () => {
    setBusy(true)
    setError(null)
    try {
      const provider = status?.billingProvider
      if (provider === 'none') {
        setError('Self-serve billing is not available for this account.')
        return
      }
      if (provider === 'stripe') {
        history.push('/billing')
        return
      }
      const result = await openShopifySelectPlan(status)
      if (!result.ok) setError(result.error || 'Could not open Shopify plan selection')
    } finally {
      setBusy(false)
    }
  }, [status, history])

  const startCheckout = useCallback(async (plan: string, interval: 'month' | 'year') => {
    setBusy(true)
    setError(null)
    try {
      const result = await startStripeCheckout({ plan, interval })
      if (!result.ok) setError(result.error || 'Could not start card checkout')
    } finally {
      setBusy(false)
    }
  }, [])

  const viewBilling = useCallback(async () => {
    setBusy(true)
    setError(null)
    try {
      if (status?.billingProvider === 'stripe') {
        const result = await startStripePortal()
        if (!result.ok) setError(result.error || 'Could not open Stripe billing portal')
        return
      }
      if (status?.billingProvider !== 'shopify') {
        setError('Shopify organization billing is only available for linked shops.')
        return
      }
      const result = await openShopifyViewBilling(status)
      if (!result.ok) setError(result.error || 'Could not open Shopify organization billing')
    } finally {
      setBusy(false)
    }
  }, [status])

  // Back-compat name for a single primary CTA (Select plan / Stripe checkout page)
  const subscribe = selectPlan

  return { status, error, busy, reload, subscribe, selectPlan, viewBilling, startCheckout }
}
